import { useEffect, useState } from 'react';

import getOrders from '@/shared/api/orders/getOrders';
import getTemplateSchemas from '@/shared/api/shipping/profiles/getTemplateSchemas';
import viewShippingProfiles from '@/shared/api/shipping/profiles/viewShippingProfiles';
import useRedirectLogin from '@/shared/hooks/useRedirectLogin';
import Order from '@/shared/types/order/order.type';
import ProfilesTemplateSchemas from '@/shared/types/shipping/profilesTemplateSchemas.type';
import ShippingProfile from '@/shared/types/shipping/shippingProfile.type';

type Args = {
  orderId: string;
};

const useFetchData = ({ orderId }: Args) => {
  const redirectLogin = useRedirectLogin();

  const [order, setOrder] = useState<Order | null>(null);

  const [shippingProfile, setShippingProfile] =
    useState<ShippingProfile | null>(null);

  const [profileTemplates, setProfileTemplates] =
    useState<ProfilesTemplateSchemas | null>(null);

  useEffect(() => {
    const fetchOrder = async () => {
      const response = await getOrders(
        {
          search: [
            {
              in_order_id: {
                cop: ['='],
                value: orderId,
              },
            },
          ],
        },
        redirectLogin,
      );

      if (response.success) {
        setOrder(response.data[0]);
      }
    };

    void fetchOrder();
  }, [orderId, redirectLogin]);

  useEffect(() => {
    const fetchShippingProfile = async () => {
      const response = await viewShippingProfiles({
        body: {
          search: [
            {
              shipping_profile_id: {
                cop: ['='],
                value: order?.shipping_profile_id,
              },
            },
          ],
        },
        onUnauthorized: redirectLogin,
      });

      if (response.success) {
        setShippingProfile(response.data[0]);
      }
    };

    if (order) {
      void fetchShippingProfile();
    }
  }, [order, redirectLogin]);

  useEffect(() => {
    const fetchShippingProfileTemplates = async () => {
      const response = await getTemplateSchemas({
        providerKey: shippingProfile?.shipping_provider_key || '',
        onUnauthorized: redirectLogin,
      });

      if (response.success) {
        setProfileTemplates(response.data);
      }
    };

    if (shippingProfile) {
      void fetchShippingProfileTemplates();
    }
  }, [shippingProfile, redirectLogin]);

  return {
    order,
    shippingProfile,
    profileTemplates,
  };
};

export default useFetchData;
